<template>
	<aside v-if="hasContent" class="article-sidebar">
		<!-- 老师名片 -->
		<section v-if="teacherInfo" class="teacher-card">
			<div class="teacher-card__avatar-wrap">
				<img :src="teacherInfo.avatar" :alt="teacherInfo.nickname" class="teacher-card__avatar" />
			</div>
			<h3 class="teacher-card__name">{{ teacherInfo.nickname }}</h3>
			<div v-if="teacherInfo.tags?.length" class="teacher-card__tags">
				<span v-for="(tag, index) in teacherInfo.tags" :key="index" class="teacher-card__tag">
					{{ tag }}
				</span>
			</div>
			<div class="teacher-card__stats">
				<div class="teacher-card__stat">
					<span class="teacher-card__stat-value">{{ teacherInfo.years }}</span>
					<span class="teacher-card__stat-label">{{
						t("square.detail.articleSidebar.yearsExperience")
					}}</span>
				</div>
				<div class="teacher-card__stat">
					<span class="teacher-card__stat-value">{{ teacherInfo.serviceCount }}</span>
					<span class="teacher-card__stat-label">{{
						t("square.detail.articleSidebar.serviceCount")
					}}</span>
				</div>
				<div class="teacher-card__stat">
					<span class="teacher-card__stat-value">{{ teacherInfo.rating }}</span>
					<span class="teacher-card__stat-label">{{
						t("square.detail.articleSidebar.rating")
					}}</span>
				</div>
			</div>
			<p v-if="teacherInfo.intro" class="teacher-card__intro">{{ teacherInfo.intro }}</p>
			<button type="button" class="teacher-card__contact-btn" @click="handleContactTeacher">
				{{ t("square.detail.articleSidebar.contactTeacher") }}
			</button>
		</section>

		<!-- 热门内容 -->
		<section v-if="hotList.length" class="hot-content">
			<h3 class="hot-content__title">{{ t("square.detail.articleSidebar.hotTitle") }}</h3>
			<ol class="hot-content__list">
				<li
					v-for="(item, index) in hotList"
					:key="item.id"
					:ref="el => bindHotRef(el, item, index)"
					class="hot-content__item"
					@click="handleToDetail(item, index)"
				>
					<span class="hot-content__rank" :class="`hot-content__rank--${Math.min(index + 1, 4)}`">
						{{ index + 1 }}
					</span>
					<p class="hot-content__item-title">{{ item.title }}</p>
					<div class="hot-content__views">
						<span>{{ formatViewCount(item.view_count) }}</span>
						<img :src="viewIcon" alt="" class="hot-content__views-icon" aria-hidden="true" />
					</div>
				</li>
			</ol>
		</section>
	</aside>
</template>

<script setup>
import { computed, inject } from "vue";
import { getUserDetail, playContent, getTeacherRecommend } from "~/api/api";
import { useI18n } from "~/composables/useI18n";
import {
	buildAuthorContactParams,
	buildHotContentParams,
	useDetailListImpression,
} from "../../../composables/useSquareDetailTracking";
import viewIcon from "../../../images/user.png";

const MAX_HOT_COUNT = 8;
const MAX_TAG_COUNT = 3;

const props = defineProps({
	squareInfo: {
		type: Object,
		default: () => ({}),
	},
	squareId: {
		type: String,
		default: "",
	},
});

const { t, locale } = useI18n();
const logDetailEvent = inject("logDetailEvent", null);

const { bindItemRef: bindHotRef } = useDetailListImpression({
	logDetailEvent,
	impressionEvent: "article_hot_content_impression",
	getItemParams: (item, index) =>
		buildHotContentParams(props.squareInfo, item, {
			rank: index + 1,
			position: index + 1,
		}),
});

const { data: teacherDetail } = await useLazyAsyncData(
	() => `square-detail-teacher-${props.squareId}-${props.squareInfo?.uid || ""}`,
	async () => {
		const uid = props.squareInfo?.uid;
		if (!uid) return null;

		try {
			const res = await getUserDetail({ uid: String(uid) });
			return res?.data || null;
		} catch (error) {
			console.error("获取老师信息失败:", error);
			return null;
		}
	},
	{
		default: () => null,
		watch: [() => props.squareInfo?.uid, () => props.squareId],
	}
);

const teacherInfo = computed(() => {
	const data = teacherDetail.value;
	if (!data?.uid) return null;

	const tags = data.academic_sector?.length
		? data.academic_sector
		: Array.isArray(data.tags)
			? data.tags
			: [];

	return {
		uid: data.uid,
		nickname: data.nickname || "",
		avatar: data.avatar || "",
		tags: tags.slice(0, MAX_TAG_COUNT),
		years: data.year || "--",
		serviceCount: data.answers ?? "--",
		rating: data.score ?? "--",
		intro: data.introduce || "",
	};
});

const { data: hotRecommendData } = await useLazyAsyncData(
	() => `square-detail-hot-${props.squareId}-${props.squareInfo?.uid || ""}`,
	async () => {
		const uid = props.squareInfo?.uid;
		if (!uid) return [];

		try {
			const res = await getTeacherRecommend({
				uid: String(uid),
				type: "2",
				page: "1",
			});
			const list = res?.data?.list;
			const currentId = String(props.squareId || props.squareInfo?.id || "");
			return Array.isArray(list)
				? list.filter(item => String(item.id) !== currentId).slice(0, MAX_HOT_COUNT)
				: [];
		} catch (error) {
			console.error("获取热门内容失败:", error);
			return [];
		}
	},
	{
		default: () => [],
		watch: [() => props.squareInfo?.uid, () => props.squareId],
	}
);

const hotList = computed(() => {
	const isEn = locale.value === "en";
	return (hotRecommendData.value || []).map(item => ({
		...item,
		title: (isEn ? item.title_en : item.title) || item.title || "",
	}));
});

const hasContent = computed(() => Boolean(teacherInfo.value) || hotList.value.length > 0);

const formatViewCount = count => {
	const value = Number(count) || 0;
	if (value >= 10000) {
		if (locale.value === "en") {
			return `${(value / 1000).toFixed(1)}k`;
		}
		return `${(value / 10000).toFixed(1)}万`;
	}
	if (value >= 1000) {
		return `${(value / 1000).toFixed(1)}k`;
	}
	return String(value);
};

const handleContactTeacher = () => {
	if (!teacherInfo.value?.uid) return;
	if (logDetailEvent) {
		logDetailEvent(
			"article_author_contact_click",
			buildAuthorContactParams(props.squareInfo, teacherInfo.value.uid)
		);
	}
	navigateTo(`/seer/teacher-detail/${teacherInfo.value.uid}`);
};

const handleToDetail = async (item, index) => {
	if (!item?.id) return;
	if (logDetailEvent) {
		logDetailEvent(
			"article_hot_content_click",
			buildHotContentParams(props.squareInfo, item, {
				rank: index + 1,
				position: index + 1,
			})
		);
	}
	await navigateTo(`/seer/square-detail/${item.id}`);
	await playContent({ square_id: item.id });
};
</script>

<style lang="scss" scoped>
@mixin line-clamp($lines: 2) {
	display: -webkit-box;
	-webkit-line-clamp: $lines;
	-webkit-box-orient: vertical;
	overflow: hidden;
	text-overflow: ellipsis;
}

.article-sidebar {
	display: flex;
	flex-direction: column;
	gap: 24px;
	padding-left: 45px;
}

.teacher-card {
	padding: 32px 24px 24px;
	background: #ffffff;
	border: 1px solid #efefef;
	border-radius: 12px;
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
	text-align: center;

	&__avatar-wrap {
		display: flex;
		justify-content: center;
		margin-bottom: 16px;
	}

	&__avatar {
		width: 76px;
		height: 76px;
		border-radius: 50%;
		object-fit: cover;
		background: #f0f0f0;
	}

	&__name {
		margin: 0 0 14px;
		font-size: 20px;
		font-weight: 700;
		color: #333333;
		line-height: 1.3;
	}

	&__tags {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: 8px;
		margin-bottom: 20px;
	}

	&__tag {
		padding: 2px 10px;
		font-size: 15px;
		color: #e09109;
		background: #fff9e5;
		border-radius: 4px;
		line-height: 1.5;
	}

	&__stats {
		display: flex;
		justify-content: space-between;
		gap: 8px;
		margin-bottom: 20px;
		padding: 0 4px;
	}

	&__stat {
		flex: 1;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 6px;
		min-width: 0;
	}

	&__stat-value {
		font-size: 20px;
		font-weight: 700;
		color: #333333;
		line-height: 1.2;
	}

	&__stat-label {
		font-size: 14px;
		color: #595959;
		line-height: 1.3;
		white-space: nowrap;
	}

	&__intro {
		margin: 0 0 20px;
		font-size: 16px;
		color: #595959;
		line-height: 1.5;
		text-align: left;
	}

	&__contact-btn {
		width: 100%;
		height: 52px;
		border: none;
		border-radius: 8px;
		background: #333333;
		color: #ffffff;
		font-size: 18px;
		font-weight: 500;
		cursor: pointer;
		transition: opacity 0.2s ease;
		margin-top: 20px;

		&:hover {
			opacity: 0.88;
		}
	}
}

.hot-content {
	padding: 24px 20px;
	background: #ffffff;
	border: 1px solid #efefef;
	border-radius: 12px;
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);

	&__title {
		margin: 0 0 20px;
		font-size: 24px;
		font-weight: 700;
		color: #333333;
		line-height: 1.3;
	}

	&__list {
		list-style: none;
		margin: 0;
		padding: 0;
	}

	&__item {
		display: flex;
		align-items: center;
		gap: 12px;
		padding: 12px 0;
		cursor: pointer;
		border-bottom: 1px solid #f5f5f5;

		&:last-child {
			border-bottom: none;
			padding-bottom: 0;
		}

		&:first-child {
			padding-top: 0;
		}

		&:hover .hot-content__item-title {
			color: #b9703a;
		}
	}

	&__rank {
		flex-shrink: 0;
		width: 25px;
		height: 25px;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 14px;
		color: #595959;
		border: 1px solid #e7e7e7;

		&--1 {
			color: #f2af19;
			border-color: #f2af19;
		}

		&--2 {
			color: #6392d7;
			border-color: #6392d7;
		}

		&--3 {
			color: #c8843c;
			border-color: #c8843c;
		}
	}

	&__item-title {
		flex: 1;
		min-width: 0;
		margin: 0;
		font-size: 16px;
		font-weight: 500;
		color: #333333;
		line-height: 1.5;
		transition: color 0.2s ease;
		@include line-clamp(1);
	}

	&__views {
		flex-shrink: 0;
		display: flex;
		align-items: center;
		gap: 4px;
		font-size: 13px;
		color: #a0a0a0;
	}

	&__views-icon {
		width: 14px;
		height: 14px;
		object-fit: contain;
	}
}
</style>
